import fileUploadRequester from '../utils/file-upload-requester';
import superFetch from '../utils/super-fetch';
import authService from './auth-service';
/* global CONFIG */
const { apiUrl } = CONFIG;
const attachmentExtractRequests = {};
const DEAL_EXTRACTION_POLL_INTERVAL = 30000;
const OPTIMISTIC_STATUS_GRACE_BUFFER_MS = 10000;
const MAX_INITIAL_DELAY_FOR_OPTIMISTIC_MS = DEAL_EXTRACTION_POLL_INTERVAL;
const dealExtractionPollers = {};
const dealExtractionReadyByAttachments = {};
const nowProvider = { now: () => Date.now() };

const getCurrentTime = () => nowProvider.now();

const markDealExtractionReady = (dealId) => {
  if (!dealId && dealId !== 0) return;
  const normalizedDealId = String(dealId);
  dealExtractionReadyByAttachments[normalizedDealId] = true;
};

const isDealExtractionReady = dealId => Boolean(dealExtractionReadyByAttachments[String(dealId)]);

const clearDealExtractionReady = (dealId) => {
  if (!dealId && dealId !== 0) return;
  delete dealExtractionReadyByAttachments[String(dealId)];
};

const deleteAttachment = (fileId) => {
  const url = `${apiUrl}/mbb/attachments/${fileId}`;
  const options = {
    method: 'DELETE',
  };
  return superFetch(url, options)
    .then(data => data);
};

// initial fetch for attachments on load of Files page
const fetchAttachments = (params) => {
  const url = `${apiUrl}/mbb/attachments`;
  const options = {
    method: 'GET',
    params,
  };
  return superFetch(url, options)
    .then(data => data.json())
    .then((data) => {
      if (params && (params.deal || params.deal === 0)) {
        markDealExtractionReady(params.deal);
      }
      return data || [];
    });
};

const fetchAttachmentsDownloadLink = (fileId, forceDownload) => {
  let url = `${apiUrl}/mbb/attachments/${fileId}/download`;
  let newWindow;
  const options = {
    method: 'GET',
  };
  if (!forceDownload) {
    // This is here so it will prevent pop up blockers. Pop up blockers
    // tend to be initiated by code that runs after ajax requests.
    // If you initiate some of the code before
    // the ajax call then it will allow it.
    newWindow = window.open('', '_blank');
    url = `${apiUrl}/mbb/attachments/${fileId}/view`;
  }
  return superFetch(url, options)
    .then(res => res.json())
    .then((res) => {
      if (res.attachment_download_url) {
        const link = document.createElement('a');
        link.setAttribute('href', res.attachment_download_url);
        link.download = 'download';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } else {
        newWindow.location.replace(res.attachment_download_url);
      }
      return res;
    });
};

const fetchAttachmentsViewLink = (fileId) => {
  const url = `${apiUrl}/mbb/attachments/${fileId}/view`;
  const options = {
    method: 'GET',
  };

  return superFetch(url, options)
    .then(res => res.json())
    .then((res) => {
      if (res.attachment_download_url) {
        return res.attachment_download_url;
      }
      throw new Error('No attachment download URL found');
    });
};

const fetchAttachmentUploadData = (id) => {
  const url = `${apiUrl}/mbb/attachments/${id}/upload`;
  const options = {
    method: 'GET',
  };
  return superFetch(url, options)
    .then(data => data.json());
};

const postAttachments = (params) => {
  const url = `${apiUrl}/mbb/attachments`;
  const options = {
    method: 'POST',
  };
  return superFetch(url, options, JSON.stringify(params))
    .then(data => data.json());
};

const isOfacFileName = filename => Boolean(filename && filename.toLowerCase().includes('ofac'));

const uploadAttachment = (file, data) => {
  // Check if form data is supported in the browser
  if (FormData) {
    // Extract file extension from original filename
    const getFileExtension = (filename) => {
      const lastDot = filename.lastIndexOf('.');
      return lastDot !== -1 ? filename.substring(lastDot) : '';
    };
    const stripFileExtension = (filename) => {
      const lastDot = filename.lastIndexOf('.');
      return lastDot !== -1 ? filename.substring(0, lastDot) : filename;
    };

    // Ensure document_name includes the file extension
    const userProvidedName = data.title || data.document_name || file.name;
    const documentName = getFileExtension(userProvidedName)
      ? userProvidedName
      : userProvidedName + getFileExtension(file.name);
    const title = stripFileExtension(documentName);
    const isOfacDocument = [title, documentName, file.name].some(isOfacFileName);
    const formData = new FormData();
    formData.append(
      'is_lease_document',
      data.is_lease_document === true ? 'True' : 'False',
    );
    formData.append('deal_id', parseInt(data.deal, 10));
    formData.append('file_size', parseInt(file.size, 10));
    formData.append('title', title);
    formData.append('document_name', documentName);
    formData.append('privacy', data.privacy);
    formData.append('file_type', file.type); // Use file.type instead of file.file_type
    formData.append('file_content', file); // Actual file content
    formData.append(
      'extraction_opted_in',
      data.extraction_opted_in === true && !isOfacDocument ? 'True' : 'False',
    );
    const url = `${apiUrl}/mbb/attachments_upload`;
    const options = {
      method: 'POST',
      body: formData,
      headers: { Authorization: `JWT ${authService.getToken()}` }, // Do not set Content-Type, browser will handle it
    };
    return superFetch(url, options, formData).then(response => response.json());
  }
  // Return error if FormData is not supported
  return Promise.reject(new Error('FormData is not supported in this browser.'));
};

const postAttachmentsToS3 = (file, params, onProgress) => {
  const { fields } = params.attachment_upload_data;
  const { url } = params.attachment_upload_data;
  return fileUploadRequester(url, file, fields, onProgress);
};

const postAttachmentsToExtract = (params) => {
  const url = `${apiUrl}/mbb/attachmentextract`;
  const shouldUseAbstractQuery = Boolean(params && params.is_abstract === true);
  const requestBody = shouldUseAbstractQuery
    ? {
      ...params,
      // Keep is_abstract in payload when it is also sent in query params.
      is_abstract: true,
      attachment_file_key: params.attachment_file_key === null || typeof params.attachment_file_key === 'undefined'
        ? undefined
        : params.attachment_file_key,
    }
    : params;
  const options = {
    method: 'POST',
    params: shouldUseAbstractQuery
      ? {
        deal: params.deal,
        file: params.file,
        is_abstract: true,
      }
      : undefined,
  };
  return superFetch(url, options, JSON.stringify(requestBody))
    .then(data => data.json());
};

const getAttachmentsExtractRequestKey = (params) => {
  if (params?.deal && params?.file) return `${params.deal}:${params.file}`;
  if (params?.deal) return `deal:${params.deal}`;
  if (params?.file) return `file:${params.file}`;
  return null;
};

const getAttachmentsExtract = (params) => {
  const requestKey = getAttachmentsExtractRequestKey(params);
  if (requestKey && attachmentExtractRequests[requestKey]) {
    return attachmentExtractRequests[requestKey];
  }
  const url = `${apiUrl}/mbb/attachmentextract`;
  const options = {
    method: 'GET',
    params,
  };
  const request = superFetch(url, options)
    .then(data => data.json());

  if (requestKey) {
    attachmentExtractRequests[requestKey] = request;
    request.then(
      () => { delete attachmentExtractRequests[requestKey]; },
      () => { delete attachmentExtractRequests[requestKey]; },
    );
  }

  return request;
};


const getAttachmentFileId = (result) => {
  if (!result) return null;

  // Backend payloads vary by endpoint/version; normalize all known file id shapes.
  const candidates = [
    result.file,
    result.file_id,
    result.fileId,
    result.attachment_file_id,
    result.attachmentId,
    result.attachment_id,
    result.attachment,
    result.attachment_file,
    result.attachmentFile,
    result.document_id,
    result.documentId,
    result.id,
    result?.file?.id,
    result?.attachment?.id,
  ];

  const first = candidates.find(value => value !== null && typeof value !== 'undefined' && value !== '');
  return first !== undefined ? first : null;
};

const hasActiveExtractionStatus = results => results.some(item => (
  item?.status === 'Queued' || item?.status === 'Processing'
));

const notifyDealPollerListeners = (dealId) => {
  const poller = dealExtractionPollers[dealId];
  if (!poller) return;

  poller.listeners.forEach((listener) => {
    const normalizedListenerFileId = String(listener.fileId);
    let fileResult = poller.resultsByFile[normalizedListenerFileId] || null;

    // Fallback for payload variants where direct map keys differ.
    if (!fileResult && Array.isArray(poller.results) && poller.results.length > 0) {
      fileResult = poller.results.find((item) => {
        const mappedId = getAttachmentFileId(item);
        return mappedId !== null && typeof mappedId !== 'undefined' && String(mappedId) === normalizedListenerFileId;
      }) || null;
    }

    // Grace-period guard: if extraction was just triggered for this file,
    // do not downgrade an in-flight Processing/Queued status to Stopped while
    // the backend is still persisting the new status row.
    const optimisticExpiry = poller.pendingOptimisticByFile[normalizedListenerFileId];
    if (fileResult && fileResult.status === 'Stopped' && optimisticExpiry) {
      if (getCurrentTime() < optimisticExpiry) {
        fileResult = { ...fileResult, status: 'Processing' };
      } else {
        delete poller.pendingOptimisticByFile[normalizedListenerFileId];
      }
    } else if (fileResult && fileResult.status !== 'Stopped' && optimisticExpiry) {
      // Stop suppressing once the backend reports a non-stopped state.
      delete poller.pendingOptimisticByFile[normalizedListenerFileId];
    }

    listener.onUpdate(fileResult, poller.results);
  });
};

const stopDealExtractionPolling = (dealId) => {
  const normalizedDealId = String(dealId);
  const poller = dealExtractionPollers[normalizedDealId];
  if (poller && poller.intervalId) {
    clearInterval(poller.intervalId);
    poller.intervalId = null;
  }
};

function refreshDealExtractionStatuses(dealId) {
  const normalizedDealId = String(dealId);
  const poller = dealExtractionPollers[normalizedDealId];
  if (!poller) return Promise.resolve([]);

  if (poller.requestInFlight) {
    return poller.requestInFlight;
  }

  poller.requestInFlight = getAttachmentsExtract({ deal: normalizedDealId })
    .then((res) => {
      const results = Array.isArray(res?.result) ? res.result : [];
      const resultsByFile = {};

      results.forEach((item) => {
        const fileId = getAttachmentFileId(item);
        if (fileId !== null && typeof fileId !== 'undefined') {
          resultsByFile[String(fileId)] = item;
        }
      });

      poller.results = results;
      poller.resultsByFile = resultsByFile;
      notifyDealPollerListeners(normalizedDealId);

      if (hasActiveExtractionStatus(results)) {
        if (!poller.intervalId) {
          poller.intervalId = setInterval(() => {
            refreshDealExtractionStatuses(normalizedDealId);
          }, DEAL_EXTRACTION_POLL_INTERVAL);
        }
      } else {
        stopDealExtractionPolling(normalizedDealId);
      }

      return results;
    })
    .catch((error) => {
      // Stop polling on fetch failure so consumers can explicitly restart.
      stopDealExtractionPolling(normalizedDealId);

      poller.listeners.forEach((listener) => {
        if (listener.onError) {
          listener.onError(error);
        }
      });
      throw error;
    })
    .finally(() => {
      const currentPoller = dealExtractionPollers[normalizedDealId];
      if (currentPoller) {
        currentPoller.requestInFlight = null;
      }
    });

  return poller.requestInFlight;
}

const ensureDealExtractionPoller = (dealId) => {
  const normalizedDealId = String(dealId);
  if (!dealExtractionPollers[normalizedDealId]) {
    dealExtractionPollers[normalizedDealId] = {
      listeners: [],
      intervalId: null,
      requestInFlight: null,
      results: [],
      resultsByFile: {},
      pendingOptimisticByFile: {},
    };
  }

  return dealExtractionPollers[normalizedDealId];
};

const subscribeToDealExtractionStatus = ({
  dealId,
  fileId,
  onUpdate,
  onError,
}) => {
  if (!dealId || !fileId || typeof onUpdate !== 'function') {
    return () => {};
  }

  const normalizedDealId = String(dealId);
  const normalizedFileId = String(fileId);
  const poller = ensureDealExtractionPoller(normalizedDealId);
  const listener = {
    fileId: normalizedFileId,
    onUpdate,
    onError,
  };

  poller.listeners.push(listener);

  const shouldRefreshOnSubscribe = isDealExtractionReady(normalizedDealId);
  if (shouldRefreshOnSubscribe) {
    // Avoid double notification on subscribe: when refreshing now, let refresh drive initial update.
    refreshDealExtractionStatuses(normalizedDealId).catch(() => {});
  } else if (poller.resultsByFile[normalizedFileId] || poller.results.length > 0) {
    onUpdate(poller.resultsByFile[normalizedFileId] || null, poller.results);
  }

  return () => {
    const currentPoller = dealExtractionPollers[normalizedDealId];
    if (!currentPoller) return;

    currentPoller.listeners = currentPoller.listeners.filter(item => item !== listener);
    if (currentPoller.listeners.length === 0) {
      stopDealExtractionPolling(normalizedDealId);
      delete dealExtractionPollers[normalizedDealId];
      clearDealExtractionReady(normalizedDealId);
    }
  };
};

const startDealExtractionPolling = (dealId, fileId, options = {}) => {
  if (!dealId && dealId !== 0) return Promise.resolve([]);
  const poller = ensureDealExtractionPoller(dealId);
  if (!isDealExtractionReady(dealId)) {
    return Promise.resolve([]);
  }

  const delayMs = Number.isFinite(options.initialDelayMs) && options.initialDelayMs > 0
    ? options.initialDelayMs
    : 0;

  // Mark this file with a grace period (one full poll interval + 10 s buffer) so
  // the immediate GET response cannot overwrite the just-triggered Processing status.
  if (fileId || fileId === 0) {
    const boundedDelayMs = Math.min(delayMs, MAX_INITIAL_DELAY_FOR_OPTIMISTIC_MS);
    poller.pendingOptimisticByFile[String(fileId)] =
      getCurrentTime() + boundedDelayMs + DEAL_EXTRACTION_POLL_INTERVAL + OPTIMISTIC_STATUS_GRACE_BUFFER_MS;
  }

  if (!delayMs) {
    return refreshDealExtractionStatuses(dealId);
  }

  return new Promise((resolve, reject) => {
    setTimeout(() => {
      refreshDealExtractionStatuses(dealId)
        .then(resolve)
        .catch(reject);
    }, delayMs);
  });
};


const updateAttachment = (id, params) => {
  const url = `${apiUrl}/mbb/attachments/${id}`;
  const options = {
    method: 'PATCH',
  };
  return superFetch(url, options, JSON.stringify(params))
    .then(data => data.json());
};

const setDealExtractionNowProvider = (provider) => {
  nowProvider.now = typeof provider === 'function' ? provider : () => Date.now();
};

const resetDealExtractionNowProvider = () => {
  nowProvider.now = () => Date.now();
};

export {
  deleteAttachment,
  fetchAttachments,
  fetchAttachmentsDownloadLink,
  fetchAttachmentUploadData,
  postAttachments,
  postAttachmentsToS3,
  postAttachmentsToExtract,
  getAttachmentsExtract,
  subscribeToDealExtractionStatus,
  startDealExtractionPolling,
  updateAttachment,
  fetchAttachmentsViewLink,
  uploadAttachment,
  // Test hooks for deterministic optimistic status timing.
  setDealExtractionNowProvider,
  resetDealExtractionNowProvider,
};
